import { Loader2 } from 'lucide-react'
import { createContext, type ReactNode, useContext, useMemo } from 'react'
import { Navigate } from 'react-router'

import {
  FeedbackNetworkError,
  FeedbackUnknownError,
} from '@/components/feedbacks'
import { useProject } from '@/contexts/project'
import { useRouteBotId } from '@/hooks/use-route-bot-id'
import { useRouteCompanyId } from '@/hooks/use-route-company-id'
import { useRouteProjectId } from '@/hooks/use-route-project-id'
import type { Bot } from '@/services/fintalk/services/fintalk-backoffice/company-bots/list-by-company'

export interface BotContextProps {
  bot: Bot
}

export const BotContext = createContext<BotContextProps | null>(null)

export function BotProvider({ children }: { children: ReactNode }) {
  const companyId = useRouteCompanyId()!
  const projectId = useRouteProjectId()!
  const botId = useRouteBotId()!

  const { bots, botsStatus, loadBots } = useProject()

  const bot = useMemo(
    () => bots.find((bot) => bot._id === botId) ?? null,
    [bots, botId],
  )

  if (botsStatus.type === 'error' && botsStatus.errorType === 'NETWORK_ERROR') {
    return (
      <div className="flex min-h-screen w-full items-center justify-center">
        <FeedbackNetworkError
          eventId={botsStatus.eventId}
          onRetry={loadBots}
          isRetrying={botsStatus.isRetrying}
        />
      </div>
    )
  }

  if (botsStatus.type === 'error') {
    return (
      <div className="flex min-h-screen w-full items-center justify-center">
        <FeedbackUnknownError
          eventId={botsStatus.eventId}
          onRetry={loadBots}
          isRetrying={botsStatus.isRetrying}
        />
      </div>
    )
  }

  if (botsStatus.type === 'success' && !bot) {
    return (
      <Navigate
        to={`/projects/${projectId}/bots?companyId=${companyId}`}
        replace
      />
    )
  }

  if (!bot) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center">
        <Loader2 className="size-8 animate-spin text-fintalk" />
      </div>
    )
  }

  return <BotContext value={{ bot }}>{children}</BotContext>
}

/**
 * Reads the current bot. The provider only renders its children once the bot
 * is resolved, so `bot` is never null here. Consumers that only need the id
 * must use useRouteBotId() instead: the URL is the source of truth for which
 * bot is selected, this provider only carries its data.
 */
export const useBot = () => {
  const context = useContext(BotContext)

  if (!context) {
    throw new Error('useBot must be used within a BotProvider')
  }

  return context
}
